import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as xdpSelectors from 'data/xdp/xdp.selectors';
import * as xdpActions from 'data/xdp/xdp.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { Xdp } from 'data/xdp/xdp.types';
import XdpForm from 'routes/Xdp/components/XdpForm';
import * as types from 'data/xdp/xdp.types';
import * as nodesSelectors from 'data/nodes/nodes.selectors';
import * as nodesActions from 'data/nodes/nodes.actions';
import { adaptToServerFormat } from 'data/xdp/xdp.adapter';
import { xdpTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateOptions,
  adaptFormData,
  adaptXdpFormData,
  adaptXdpTemplate,
} from '../../utils/xdpTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/xdpTemplateDataToSubmit';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.Xdp
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  xdpTemplateDef: adaptTemplateVisibilityMode(xdpTemplate, 'edit'),
  deliveryFunctionId: state.global.selectedDF,
  selectedXdp: xdpSelectors
    .getSelectedXdp(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedXdp: xdpSelectors
    .getSelectedXdp(state)[0],
  xdp: xdpSelectors.getXdp(state),
  nodes: nodesSelectors.getNodes(state),
});

const mapDispatch = {
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  goToXdpList: xdpActions.goToXdpList,
  updateXdp: xdpActions.updateXdp,
  getXdp: xdpActions.getXdp,
  getNodes: nodesActions.getNodes,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const XdpEdit = ({
  history,
  selectedXdp,
  originalSelectedXdp,
  deliveryFunctionId,
  userId,
  userName,
  isAuditEnabled,
  xdpTemplateDef,
  xdp,
  goToXdpList,
  updateXdp,
  getXdp,
  getNodes,
  nodes,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<Xdp>>({});
  const [template, setTemplate] = useState(xdpTemplateDef);
  const { t } = useTranslation();

  const id = selectedXdp?.id;

  useEffect(() => {
    if (id) {
      getXdp(deliveryFunctionId, id);
    }
    getNodes(deliveryFunctionId);
  }, [deliveryFunctionId, id, getXdp, getNodes]);

  useEffect(() => {
    if (xdp != null && template != null) {
      let clonedXdp = cloneDeep(xdp);
      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();

      clonedXdp = adaptXdpFormData(clonedXdp);

      setTemplate(adaptTemplateOptions(xdpTemplateDef, clonedXdp, nodes));
      setFormData(clonedXdp);
    }
  }, [xdp, id]);

  const getUpdateXdpActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const xdpFormData = adaptToServerFormat(formRef.current.getValues());
    const payload = {
      data: xdpFormData,
      auditDetails: getAuditDetailsToSubmit(
        xdpFormData,
        originalSelectedXdp,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getUpdateXdpActionPayload();
    if (payload && xdp != null) {
      updateXdp(deliveryFunctionId, xdp?.id, payload, history);
    }
  };

  const handleCancel = () => goToXdpList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as Xdp);
    let clonedTemplate = cloneDeep(template);

    let newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptXdpTemplate(clonedTemplate);

    newData = adaptFormData(newData);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <XdpForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };

  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (xdp == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="XdpEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('xdp.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(XdpEdit));
